export function createFluidSimulation({
  state,
  config,
  helpers,
  visuals
} = {}) {
  const {
    waterSourceLevel,
    waterFallLevel,
    waterMinFlowLevel,
    waterTickInterval,
    waterMaxTicksPerFrame,
    waterDirtyBudgetPerTick,
    waterSeedScanBudgetPerFrame,
    lavaSourceLevel,
    lavaMinFlowLevel,
    lavaMaxLateralSpread,
    lavaTickInterval,
    lavaMaxTicksPerFrame,
    lavaDirtyBudgetPerTick,
    lavaSeedScanBudgetPerFrame,
    fluidSeedEdgeMargin,
    waterVisualUpdateBudgetPerFluidTick,
    lavaIgniteScanPerSpread,
    worldMinY,
    chunkSize,
    viewDistance
  } = config;
  const {
    blockKey,
    worldToBlock,
    worldToChunk,
    parseBlockKey,
    chunkKey,
    getBlockIdAt,
    getBlockAt,
    getBlockRecordAtKey,
    createBlock,
    removeBlock,
    isChunkReadyAt,
    hasWaterContact,
    hasLavaContact,
    tryIgniteFromLavaAt,
    getActiveViewDistance,
    loadedChunks,
    solidBlocks,
    blockData,
    onLavaHardened
  } = helpers;

  function getWaterLevelAt(x, y, z) {
    const key = blockKey(worldToBlock(x), worldToBlock(y), worldToBlock(z));
    const level = Number(state.waterMap.get(key));
    return Number.isFinite(level) && Math.floor(level) >= waterMinFlowLevel && Math.floor(level) <= waterFallLevel
      ? Math.floor(level)
      : -1;
  }

  function getLavaLevelAt(x, y, z) {
    const key = blockKey(worldToBlock(x), worldToBlock(y), worldToBlock(z));
    const level = Number(state.lavaMap.get(key));
    if (!Number.isFinite(level)) return -1;
    return Math.max(lavaMinFlowLevel, Math.min(lavaSourceLevel, Math.floor(level)));
  }

  function isLavaSourceAt(x, y, z) {
    const key = blockKey(worldToBlock(x), worldToBlock(y), worldToBlock(z));
    return state.lavaSourceSet.has(key);
  }

  function canWaterExistAt(x, y, z) {
    const gx = worldToBlock(x);
    const gy = worldToBlock(y);
    const gz = worldToBlock(z);
    if (gy <= worldMinY) return false;
    if (!isChunkReadyAt(gx, gz)) return false;
    const id = getBlockIdAt(gx, gy, gz);
    if (id === null || id === 7) return true;
    if (id === 69) return false;
    return !!blockData[id]?.replaceable;
  }

  function canLavaExistAt(x, y, z) {
    const gx = worldToBlock(x);
    const gy = worldToBlock(y);
    const gz = worldToBlock(z);
    if (gy <= worldMinY) return false;
    if (!isChunkReadyAt(gx, gz)) return false;
    const id = getBlockIdAt(gx, gy, gz);
    if (id === null || id === 69) return true;
    if (id === 7) return false;
    return !!blockData[id]?.replaceable;
  }

  function isSolidCell(x, y, z) {
    const id = getBlockIdAt(x, y, z);
    if (id === null || id === 7) return false;
    const def = blockData[id];
    return !!def?.solid && !def?.replaceable;
  }

  function pendingPriority(level) {
    if (level === waterSourceLevel) return 4;
    if (level === waterFallLevel) return 3;
    if (level >= waterMinFlowLevel && level <= waterSourceLevel - 1) return 2;
    if (level === 0) return 1;
    return 0;
  }

  function writePending(pendingChanges, x, y, z, newLevel) {
    const level = Math.floor(Number(newLevel));
    if (!Number.isFinite(level)) return;
    const key = blockKey(worldToBlock(x), worldToBlock(y), worldToBlock(z));
    const nextPriority = pendingPriority(level);
    if (nextPriority <= 0) return;

    const existing = pendingChanges.get(key);
    if (!existing) {
      pendingChanges.set(key, level);
      return;
    }

    const existingPriority = pendingPriority(existing);
    if (nextPriority > existingPriority) {
      pendingChanges.set(key, level);
      return;
    }
    if (nextPriority < existingPriority) return;

    if (nextPriority === 2 && level > existing) {
      pendingChanges.set(key, level);
    }
  }

  function writePendingLava(pendingChanges, x, y, z, newLevel, source = false) {
    const level = Math.floor(Number(newLevel));
    const sourceFlag = !!source;
    const normalizedLevel = level <= 0 ? 0 : Math.max(lavaMinFlowLevel, Math.min(lavaSourceLevel, level));
    const key = blockKey(worldToBlock(x), worldToBlock(y), worldToBlock(z));
    const nextPriority = normalizedLevel <= 0 ? 0 : ((sourceFlag ? 100 : 0) + normalizedLevel);

    const existing = pendingChanges.get(key);
    if (!existing) {
      pendingChanges.set(key, { level: normalizedLevel, source: sourceFlag });
      return;
    }

    const existingPriority = existing.level <= 0 ? 0 : ((existing.source ? 100 : 0) + existing.level);
    if (nextPriority >= existingPriority) {
      pendingChanges.set(key, { level: normalizedLevel, source: sourceFlag });
    }
  }

  function queueWaterUpdateAt(x, y, z, _delay = 0) {
    const gx = worldToBlock(x);
    const gy = worldToBlock(y);
    const gz = worldToBlock(z);
    state.dirtyWaterSet.add(blockKey(gx, gy, gz));
  }

  function queueWaterUpdateAround(x, y, z, _delay = 0) {
    const gx = worldToBlock(x);
    const gy = worldToBlock(y);
    const gz = worldToBlock(z);
    for (const [dx, dy, dz] of config.waterDirtyOffsets) {
      state.dirtyWaterSet.add(blockKey(gx + dx, gy + dy, gz + dz));
    }
  }

  function queueLavaUpdateAt(x, y, z, _delay = 0) {
    const gx = worldToBlock(x);
    const gy = worldToBlock(y);
    const gz = worldToBlock(z);
    state.dirtyLavaSet.add(blockKey(gx, gy, gz));
  }

  function queueLavaUpdateAround(x, y, z, _delay = 0) {
    const gx = worldToBlock(x);
    const gy = worldToBlock(y);
    const gz = worldToBlock(z);
    for (const [dx, dy, dz] of config.waterDirtyOffsets) {
      state.dirtyLavaSet.add(blockKey(gx + dx, gy + dy, gz + dz));
    }
  }

  function hasAdjacentWaterAt(x, y, z) {
    for (const [dx, dy, dz] of config.waterDirtyOffsets) {
      if (dx === 0 && dy === 0 && dz === 0) continue;
      if (getBlockIdAt(x + dx, y + dy, z + dz) === 7) return true;
    }
    return false;
  }

  function applyWaterLevelAt(x, y, z, level) {
    const gx = worldToBlock(x);
    const gy = worldToBlock(y);
    const gz = worldToBlock(z);
    const key = blockKey(gx, gy, gz);
    const currentLevelRaw = Number(state.waterMap.get(key));
    const currentLevel = Number.isFinite(currentLevelRaw)
      && Math.floor(currentLevelRaw) >= waterMinFlowLevel
      && Math.floor(currentLevelRaw) <= waterFallLevel
      ? Math.floor(currentLevelRaw)
      : -1;
    const nextLevel = Number(level) <= 0 ? 0 : visuals.normalizeWaterLevel(level);

    if (nextLevel <= 0) {
      const existing = getBlockAt(gx, gy, gz);
      if (currentLevel < 0 && existing?.userData?.id !== 7) return false;
      if (existing?.userData?.id === 7) {
        removeBlock(existing, { trackModification: false, skipTorchCleanup: true });
      } else {
        state.waterMap.delete(key);
      }
      return true;
    }

    if (!canWaterExistAt(gx, gy, gz)) return false;

    const existing = getBlockAt(gx, gy, gz);
    if (existing?.userData?.id === 7) {
      if (currentLevel === nextLevel) return false;
      existing.userData.waterLevel = nextLevel;
      existing.userData.waterSource = nextLevel === waterSourceLevel;
      existing.userData.waterAnchoredSource = false;
      visuals.applyWaterVisualHeight(existing, nextLevel, false);
      state.waterMap.set(key, nextLevel);
      return true;
    }

    const ck = chunkKey(worldToChunk(gx), worldToChunk(gz));
    const placed = createBlock(gx, gy, gz, 7, {
      generated: true,
      chunkKey: ck,
      allowReplace: true,
      waterLevel: nextLevel
    });
    return !!placed;
  }

  function getLavaSolidifiedBlockIdAt(x, y, z) {
    return isLavaSourceAt(x, y, z) ? 31 : 5;
  }

  function hardenLavaAt(x, y, z, blockId = 5) {
    const gx = worldToBlock(x);
    const gy = worldToBlock(y);
    const gz = worldToBlock(z);
    if (getBlockIdAt(gx, gy, gz) !== 69) return false;
    const ck = chunkKey(worldToChunk(gx), worldToChunk(gz));
    const placed = createBlock(gx, gy, gz, blockId, {
      generated: true,
      chunkKey: ck,
      allowReplace: true
    });
    if (placed) onLavaHardened?.();
    return !!placed;
  }

  function applyLavaLevelAt(x, y, z, level, source = false) {
    const gx = worldToBlock(x);
    const gy = worldToBlock(y);
    const gz = worldToBlock(z);
    const key = blockKey(gx, gy, gz);
    const currentLevel = getLavaLevelAt(gx, gy, gz);
    const currentSource = state.lavaSourceSet.has(key);
    const nextLevelRaw = Math.floor(Number(level));
    const nextLevel = nextLevelRaw <= 0 ? 0 : Math.max(lavaMinFlowLevel, Math.min(lavaSourceLevel, nextLevelRaw));
    const nextSource = nextLevel > 0 && !!source;

    if (nextLevel <= 0) {
      const existing = getBlockAt(gx, gy, gz);
      if (currentLevel < 0 && existing?.userData?.id !== 69) return false;
      if (existing?.userData?.id === 69) {
        removeBlock(existing, { trackModification: false, skipTorchCleanup: true });
      } else {
        state.lavaMap.delete(key);
        state.lavaSourceSet.delete(key);
      }
      return true;
    }

    if (!canLavaExistAt(gx, gy, gz)) return false;

    const existing = getBlockAt(gx, gy, gz);
    if (existing?.userData?.id === 69) {
      if (currentLevel === nextLevel && currentSource === nextSource) return false;
      existing.userData.lavaLevel = nextLevel;
      existing.userData.lavaSource = nextSource;
      visuals.applyLavaVisualHeight(existing, nextLevel);
      visuals.applyLavaLightState(existing, nextSource);
      state.lavaMap.set(key, nextLevel);
      if (nextSource) state.lavaSourceSet.add(key);
      else state.lavaSourceSet.delete(key);
      return true;
    }

    const ck = chunkKey(worldToChunk(gx), worldToChunk(gz));
    const placed = createBlock(gx, gy, gz, 69, {
      generated: true,
      chunkKey: ck,
      allowReplace: true,
      lavaLevel: nextLevel,
      lavaSource: nextSource
    });
    return !!placed;
  }

  function enqueueWaterSeedKey(key) {
    if (state.pendingWaterSeedSet.has(key)) return;
    state.pendingWaterSeedSet.add(key);
    state.pendingWaterSeedKeys.push(key);
  }

  function enqueueLavaSeedKey(key) {
    if (state.pendingLavaSeedSet.has(key)) return;
    state.pendingLavaSeedSet.add(key);
    state.pendingLavaSeedKeys.push(key);
  }

  function dequeueWaterSeedKey() {
    if (state.pendingWaterSeedCursor >= state.pendingWaterSeedKeys.length) return null;
    const key = state.pendingWaterSeedKeys[state.pendingWaterSeedCursor];
    state.pendingWaterSeedCursor += 1;
    state.pendingWaterSeedSet.delete(key);
    if (
      state.pendingWaterSeedCursor >= 256
      && state.pendingWaterSeedCursor * 2 >= state.pendingWaterSeedKeys.length
    ) {
      state.pendingWaterSeedKeys.splice(0, state.pendingWaterSeedCursor);
      state.pendingWaterSeedCursor = 0;
    }
    return key;
  }

  function dequeueLavaSeedKey() {
    if (state.pendingLavaSeedCursor >= state.pendingLavaSeedKeys.length) return null;
    const key = state.pendingLavaSeedKeys[state.pendingLavaSeedCursor];
    state.pendingLavaSeedCursor += 1;
    state.pendingLavaSeedSet.delete(key);
    if (
      state.pendingLavaSeedCursor >= 192
      && state.pendingLavaSeedCursor * 2 >= state.pendingLavaSeedKeys.length
    ) {
      state.pendingLavaSeedKeys.splice(0, state.pendingLavaSeedCursor);
      state.pendingLavaSeedCursor = 0;
    }
    return key;
  }

  function isChunkEdgeFluidSeedCandidate(x, z, cx, cz, margin = fluidSeedEdgeMargin) {
    const localX = worldToBlock(x) - (cx * chunkSize);
    const localZ = worldToBlock(z) - (cz * chunkSize);
    return localX <= margin
      || localX >= chunkSize - 1 - margin
      || localZ <= margin
      || localZ >= chunkSize - 1 - margin;
  }

  function queueWaterSeedsForChunk(ck) {
    const chunk = loadedChunks.get(ck);
    if (!chunk?.ready) return;
    for (const key of chunk.blockKeys) {
      const mesh = solidBlocks.get(key);
      if (mesh?.userData?.id !== 7) continue;
      const { x, z } = parseBlockKey(key);
      if (!isChunkEdgeFluidSeedCandidate(x, z, chunk.cx, chunk.cz)) continue;
      enqueueWaterSeedKey(key);
    }
  }

  function queueLavaSeedsForChunk(ck) {
    const chunk = loadedChunks.get(ck);
    if (!chunk?.ready) return;
    for (const key of chunk.blockKeys) {
      const mesh = solidBlocks.get(key);
      if (mesh?.userData?.id !== 69) continue;
      const { x, z } = parseBlockKey(key);
      if (!isChunkEdgeFluidSeedCandidate(x, z, chunk.cx, chunk.cz)) continue;
      enqueueLavaSeedKey(key);
    }
  }

  function queueFluidSeedsForChunkAndReadyNeighbors(cx, cz) {
    const neighborOffsets = [
      [0, 0],
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1]
    ];

    for (const [dx, dz] of neighborOffsets) {
      const ck = chunkKey(cx + dx, cz + dz);
      const chunk = loadedChunks.get(ck);
      if (!chunk?.ready) continue;
      queueWaterSeedsForChunk(ck);
      queueLavaSeedsForChunk(ck);
    }
  }

  function isWithinFluidFocusAt(x, z, focus = null) {
    const cx = Number(focus?.cx);
    const cz = Number(focus?.cz);
    const radius = Math.max(0, Math.floor(Number(focus?.radius) || state.runtimeFluidFocusRadius || 0));
    if (!Number.isFinite(cx) || !Number.isFinite(cz)) return true;
    const qx = worldToChunk(x);
    const qz = worldToChunk(z);
    return Math.abs(qx - cx) <= radius && Math.abs(qz - cz) <= radius;
  }

  function processPendingWaterSeedQueue(maxCount = waterSeedScanBudgetPerFrame, focus = null) {
    let budget = Math.max(0, Math.floor(maxCount));
    while (budget > 0) {
      const key = dequeueWaterSeedKey();
      if (key === null) break;
      budget -= 1;
      const { x, y, z } = parseBlockKey(key);
      if (!isChunkReadyAt(x, z)) continue;
      if (getBlockIdAt(x, y, z) !== 7) continue;
      const inFocus = isWithinFluidFocusAt(x, z, focus);
      if (!inFocus && !isManualFluidBlockAt(x, y, z, 7)) {
        enqueueWaterSeedKey(key);
        continue;
      }
      queueWaterUpdateAround(x, y, z);
    }
  }

  function processPendingLavaSeedQueue(maxCount = lavaSeedScanBudgetPerFrame, focus = null) {
    let budget = Math.max(0, Math.floor(maxCount));
    while (budget > 0) {
      const key = dequeueLavaSeedKey();
      if (key === null) break;
      budget -= 1;
      const { x, y, z } = parseBlockKey(key);
      if (!isChunkReadyAt(x, z)) continue;
      if (getBlockIdAt(x, y, z) !== 69) continue;
      const inFocus = isWithinFluidFocusAt(x, z, focus);
      if (!inFocus && !isManualFluidBlockAt(x, y, z, 69)) {
        enqueueLavaSeedKey(key);
        continue;
      }
      queueLavaUpdateAround(x, y, z);
    }
  }

  function isManualFluidBlockAt(x, y, z, fluidId) {
    const key = blockKey(worldToBlock(x), worldToBlock(y), worldToBlock(z));
    const record = getBlockRecordAtKey(key);
    if (!record || record.id !== fluidId) return false;
    return record.generated === false;
  }

  function processWaterTick(maxDirtyBudget = waterDirtyBudgetPerTick, focus = null) {
    if (state.dirtyWaterSet.size <= 0) return;

    const currentDirty = state.dirtyWaterSet;
    state.dirtyWaterSet = state.processingWaterSet;
    state.processingWaterSet = currentDirty;
    state.dirtyWaterSet.clear();
    const pendingChanges = new Map();
    const dirtyBudget = Math.max(32, Math.floor(Number(maxDirtyBudget) || waterDirtyBudgetPerTick));
    let processed = 0;

    for (const key of currentDirty) {
      if (processed >= dirtyBudget) {
        state.dirtyWaterSet.add(key);
        continue;
      }
      const { x, y, z } = parseBlockKey(key);
      if (!isChunkReadyAt(x, z)) continue;
      const inFocus = isWithinFluidFocusAt(x, z, focus);
      if (!inFocus && !isManualFluidBlockAt(x, y, z, 7)) {
        state.dirtyWaterSet.add(key);
        continue;
      }
      processed += 1;

      const level = getWaterLevelAt(x, y, z);
      if (level >= 0 && !canWaterExistAt(x, y, z)) {
        writePending(pendingChanges, x, y, z, 0);
        continue;
      }

      if (level >= waterMinFlowLevel) {
        const belowLevel = getWaterLevelAt(x, y - 1, z);
        if (belowLevel < 0 && !isSolidCell(x, y - 1, z) && canWaterExistAt(x, y - 1, z)) {
          writePending(pendingChanges, x, y - 1, z, waterFallLevel);
        }

        if (isSolidCell(x, y - 1, z)) {
          let outLevel = 0;
          if (level === waterSourceLevel || level === waterFallLevel) outLevel = 6;
          else if (level >= waterMinFlowLevel && level <= 6) outLevel = level - 1;

          if (outLevel > 0) {
            for (const [dx, dy, dz] of config.waterLateralOffsets) {
              const tx = x + dx;
              const ty = y + dy;
              const tz = z + dz;
              if (!isChunkReadyAt(tx, tz)) continue;
              if (isSolidCell(tx, ty, tz)) continue;

              const targetLevel = getWaterLevelAt(tx, ty, tz);
              if (targetLevel < 0) {
                if (canWaterExistAt(tx, ty, tz)) writePending(pendingChanges, tx, ty, tz, outLevel);
                continue;
              }
              if (targetLevel < outLevel) {
                writePending(pendingChanges, tx, ty, tz, outLevel);
              }
            }
          }
        }

        if (level >= waterMinFlowLevel && level <= 6) {
          let supported = getWaterLevelAt(x, y + 1, z) >= waterMinFlowLevel;
          if (!supported) {
            for (const [dx, dy, dz] of config.waterLateralOffsets) {
              const neighborLevel = getWaterLevelAt(x + dx, y + dy, z + dz);
              if (neighborLevel > level) {
                supported = true;
                break;
              }
            }
          }
          if (!supported) {
            writePending(pendingChanges, x, y, z, level - 1);
          }
        } else if (level === waterFallLevel) {
          const supported = getWaterLevelAt(x, y + 1, z) >= waterMinFlowLevel;
          if (!supported) {
            writePending(pendingChanges, x, y, z, 0);
            queueWaterUpdateAt(x, y - 1, z);
          }
        }
      }

      const candidateForSource = (
        (level < 0 && canWaterExistAt(x, y, z))
        || (level >= waterMinFlowLevel && level <= 6)
        || level === waterFallLevel
      );
      if (!candidateForSource) continue;
      if (!isSolidCell(x, y - 1, z)) continue;

      let sourceNeighbors = 0;
      for (const [dx, dy, dz] of config.waterLateralOffsets) {
        const neighborLevel = getWaterLevelAt(x + dx, y + dy, z + dz);
        if (neighborLevel !== waterSourceLevel) continue;
        sourceNeighbors += 1;
        if (sourceNeighbors >= 2) break;
      }
      if (sourceNeighbors >= 2) {
        writePending(pendingChanges, x, y, z, waterSourceLevel);
      }
    }

    for (const [key, nextLevel] of pendingChanges.entries()) {
      const { x, y, z } = parseBlockKey(key);
      if (!isChunkReadyAt(x, z)) continue;
      const changed = applyWaterLevelAt(x, y, z, nextLevel);
      if (changed) {
        queueWaterUpdateAround(x, y, z);
        if (hasLavaContact(x, y, z)) queueLavaUpdateAround(x, y, z);
      }
    }

    currentDirty.clear();
  }

  function processLavaTick(maxDirtyBudget = lavaDirtyBudgetPerTick, focus = null) {
    if (state.dirtyLavaSet.size <= 0) return;

    const currentDirty = state.dirtyLavaSet;
    state.dirtyLavaSet = state.processingLavaSet;
    state.processingLavaSet = currentDirty;
    state.dirtyLavaSet.clear();
    const pendingChanges = new Map();
    const pendingSolidify = new Map();
    let lavaIgniteBudget = lavaIgniteScanPerSpread;
    const dirtyBudget = Math.max(20, Math.floor(Number(maxDirtyBudget) || lavaDirtyBudgetPerTick));
    let processed = 0;

    for (const key of currentDirty) {
      if (processed >= dirtyBudget) {
        state.dirtyLavaSet.add(key);
        continue;
      }
      const { x, y, z } = parseBlockKey(key);
      if (!isChunkReadyAt(x, z)) continue;
      const inFocus = isWithinFluidFocusAt(x, z, focus);
      if (!inFocus && !isManualFluidBlockAt(x, y, z, 69)) {
        state.dirtyLavaSet.add(key);
        continue;
      }
      processed += 1;

      const level = getLavaLevelAt(x, y, z);
      if (level >= lavaMinFlowLevel && !canLavaExistAt(x, y, z)) {
        writePendingLava(pendingChanges, x, y, z, 0, false);
        continue;
      }
      if (level < lavaMinFlowLevel) continue;

      const source = isLavaSourceAt(x, y, z);
      if (hasAdjacentWaterAt(x, y, z)) {
        pendingSolidify.set(key, getLavaSolidifiedBlockIdAt(x, y, z));
        continue;
      }

      if (lavaIgniteBudget > 0 && tryIgniteFromLavaAt(x, y, z, config.lavaIgniteChance)) {
        lavaIgniteBudget -= 1;
      }

      const belowLevel = getLavaLevelAt(x, y - 1, z);
      if (belowLevel < lavaMinFlowLevel && !isSolidCell(x, y - 1, z) && canLavaExistAt(x, y - 1, z)) {
        writePendingLava(pendingChanges, x, y - 1, z, lavaSourceLevel, false);
      }

      if (isSolidCell(x, y - 1, z)) {
        let outLevel = 0;
        if (source) outLevel = lavaMaxLateralSpread;
        else if (level > lavaMinFlowLevel) outLevel = Math.min(lavaMaxLateralSpread, level - 1);

        if (outLevel > 0) {
          for (const [dx, dy, dz] of config.waterLateralOffsets) {
            const tx = x + dx;
            const ty = y + dy;
            const tz = z + dz;
            if (!isChunkReadyAt(tx, tz)) continue;
            if (isSolidCell(tx, ty, tz)) continue;

            const targetLevel = getLavaLevelAt(tx, ty, tz);
            const targetSource = isLavaSourceAt(tx, ty, tz);
            if (targetLevel < lavaMinFlowLevel) {
              if (canLavaExistAt(tx, ty, tz)) writePendingLava(pendingChanges, tx, ty, tz, outLevel, false);
              continue;
            }
            if (!targetSource && targetLevel < outLevel) {
              writePendingLava(pendingChanges, tx, ty, tz, outLevel, false);
            }
          }
        }
      }

      if (!source) {
        let supported = getLavaLevelAt(x, y + 1, z) >= lavaMinFlowLevel;
        if (!supported) {
          for (const [dx, dy, dz] of config.waterLateralOffsets) {
            const tx = x + dx;
            const ty = y + dy;
            const tz = z + dz;
            if (isLavaSourceAt(tx, ty, tz)) {
              supported = true;
              break;
            }
            const neighborLevel = getLavaLevelAt(tx, ty, tz);
            if (neighborLevel > level) {
              supported = true;
              break;
            }
          }
        }
        if (!supported) {
          writePendingLava(pendingChanges, x, y, z, level - 1, false);
        }
      }
    }

    for (const [key, blockId] of pendingSolidify.entries()) {
      const { x, y, z } = parseBlockKey(key);
      if (!isChunkReadyAt(x, z)) continue;
      const changed = hardenLavaAt(x, y, z, blockId);
      if (!changed) continue;
      queueLavaUpdateAround(x, y, z);
      if (hasWaterContact(x, y, z)) queueWaterUpdateAround(x, y, z);
    }

    for (const [key, next] of pendingChanges.entries()) {
      if (pendingSolidify.has(key)) continue;
      const { x, y, z } = parseBlockKey(key);
      if (!isChunkReadyAt(x, z)) continue;
      const changed = applyLavaLevelAt(x, y, z, next.level, next.source);
      if (!changed) continue;
      queueLavaUpdateAround(x, y, z);
      if (hasWaterContact(x, y, z)) queueWaterUpdateAround(x, y, z);
    }

    currentDirty.clear();
  }

  function resolveFluidRuntimeBudgets(delta = 0.016, opts = {}) {
    const safeDelta = Math.max(0, Number(delta) || 0);
    const tierRaw = Number(opts?.performanceTier);
    const tier = Number.isFinite(tierRaw)
      ? Math.max(0, Math.min(2, Math.floor(tierRaw)))
      : 1;

    let nextWaterSeedBudget = waterSeedScanBudgetPerFrame;
    let nextLavaSeedBudget = lavaSeedScanBudgetPerFrame;
    let nextWaterDirtyBudget = waterDirtyBudgetPerTick;
    let nextLavaDirtyBudget = lavaDirtyBudgetPerTick;
    let nextWaterVisualBudget = waterVisualUpdateBudgetPerFluidTick;

    if (tier <= 0) {
      nextWaterSeedBudget = Math.max(10, Math.floor(nextWaterSeedBudget * 0.42));
      nextLavaSeedBudget = Math.max(8, Math.floor(nextLavaSeedBudget * 0.4));
      nextWaterDirtyBudget = Math.max(96, Math.floor(nextWaterDirtyBudget * 0.72));
      nextLavaDirtyBudget = Math.max(64, Math.floor(nextLavaDirtyBudget * 0.75));
      nextWaterVisualBudget = Math.max(28, Math.floor(nextWaterVisualBudget * 0.38));
    } else if (tier === 1) {
      nextWaterSeedBudget = Math.max(18, Math.floor(nextWaterSeedBudget * 0.72));
      nextLavaSeedBudget = Math.max(12, Math.floor(nextLavaSeedBudget * 0.7));
      nextWaterDirtyBudget = Math.max(144, Math.floor(nextWaterDirtyBudget * 0.88));
      nextLavaDirtyBudget = Math.max(92, Math.floor(nextLavaDirtyBudget * 0.9));
      nextWaterVisualBudget = Math.max(64, Math.floor(nextWaterVisualBudget * 0.72));
    }

    if (safeDelta > 0.024) {
      nextWaterSeedBudget = Math.max(8, Math.floor(nextWaterSeedBudget * 0.72));
      nextLavaSeedBudget = Math.max(6, Math.floor(nextLavaSeedBudget * 0.72));
      nextWaterDirtyBudget = Math.max(80, Math.floor(nextWaterDirtyBudget * 0.86));
      nextLavaDirtyBudget = Math.max(56, Math.floor(nextLavaDirtyBudget * 0.88));
      nextWaterVisualBudget = Math.max(20, Math.floor(nextWaterVisualBudget * 0.78));
    }

    const totalDirty = state.dirtyWaterSet.size + state.dirtyLavaSet.size;
    if (totalDirty > 640) {
      const focusScale = totalDirty > 1500 ? 0.5 : 0.68;
      nextWaterSeedBudget = Math.max(8, Math.floor(nextWaterSeedBudget * focusScale));
      nextLavaSeedBudget = Math.max(6, Math.floor(nextLavaSeedBudget * focusScale));
    }

    const animatingCount = state.animatingWaterMeshes.size;
    if (animatingCount > 220) {
      const animScale = animatingCount > 720 ? 0.55 : 0.72;
      nextWaterVisualBudget = Math.max(18, Math.floor(nextWaterVisualBudget * animScale));
    }

    return {
      tier,
      waterSeedBudget: nextWaterSeedBudget,
      lavaSeedBudget: nextLavaSeedBudget,
      waterDirtyBudget: nextWaterDirtyBudget,
      lavaDirtyBudget: nextLavaDirtyBudget,
      waterVisualBudget: nextWaterVisualBudget
    };
  }

  function resolveFluidFocus(opts = {}, tier = 1, delta = 0.016) {
    const px = Number(opts?.playerPosition?.x);
    const pz = Number(opts?.playerPosition?.z);
    const safeDelta = Math.max(0, Number(delta) || 0);
    if (!Number.isFinite(px) || !Number.isFinite(pz)) {
      return {
        cx: Number.NaN,
        cz: Number.NaN,
        radius: Math.max(1, getActiveViewDistance?.() ?? viewDistance)
      };
    }

    const activeViewDistance = Math.max(1, getActiveViewDistance?.() ?? viewDistance);
    let radius = tier <= 0
      ? Math.max(1, activeViewDistance - 1)
      : (tier === 1 ? Math.max(1, activeViewDistance) : Math.max(1, activeViewDistance + 1));
    if (safeDelta > 0.03 && tier <= 1) radius = Math.max(1, radius - 1);

    return {
      cx: worldToChunk(px),
      cz: worldToChunk(pz),
      radius
    };
  }

  function updateWaterFlow(delta = 0.016, opts = {}) {
    const safeDelta = Math.max(0, Number(delta) || 0);
    const budgets = resolveFluidRuntimeBudgets(safeDelta, opts);
    const fluidFocus = resolveFluidFocus(opts, budgets.tier, safeDelta);
    state.runtimeFluidPerformanceTier = budgets.tier;
    state.runtimeWaterSeedBudget = budgets.waterSeedBudget;
    state.runtimeLavaSeedBudget = budgets.lavaSeedBudget;
    state.runtimeWaterDirtyBudget = budgets.waterDirtyBudget;
    state.runtimeLavaDirtyBudget = budgets.lavaDirtyBudget;
    state.runtimeWaterVisualBudget = budgets.waterVisualBudget;
    state.runtimeFluidFocusCx = fluidFocus.cx;
    state.runtimeFluidFocusCz = fluidFocus.cz;
    state.runtimeFluidFocusRadius = fluidFocus.radius;

    visuals.updateWaterVisualAnimations(safeDelta, {
      maxUpdates: state.runtimeWaterVisualBudget,
      focus: fluidFocus,
      isWithinFluidFocusAt
    });
    processPendingWaterSeedQueue(state.runtimeWaterSeedBudget, fluidFocus);
    processPendingLavaSeedQueue(state.runtimeLavaSeedBudget, fluidFocus);

    state.waterTickAccumulator += safeDelta;
    let waterTicks = 0;
    if (state.waterTickAccumulator >= waterTickInterval) {
      waterTicks = Math.floor(state.waterTickAccumulator / waterTickInterval);
      state.waterTickAccumulator -= waterTicks * waterTickInterval;
      waterTicks = Math.min(waterTicks, waterMaxTicksPerFrame);
    }
    for (let i = 0; i < waterTicks; i++) processWaterTick(state.runtimeWaterDirtyBudget, fluidFocus);

    state.lavaTickAccumulator += safeDelta;
    let lavaTicks = 0;
    if (state.lavaTickAccumulator >= lavaTickInterval) {
      lavaTicks = Math.floor(state.lavaTickAccumulator / lavaTickInterval);
      state.lavaTickAccumulator -= lavaTicks * lavaTickInterval;
      lavaTicks = Math.min(lavaTicks, lavaMaxTicksPerFrame);
    }
    for (let i = 0; i < lavaTicks; i++) processLavaTick(state.runtimeLavaDirtyBudget, fluidFocus);
  }

  return {
    getWaterLevelAt,
    getLavaLevelAt,
    isLavaSourceAt,
    queueWaterUpdateAt,
    queueWaterUpdateAround,
    queueLavaUpdateAt,
    queueLavaUpdateAround,
    enqueueWaterSeedKey,
    enqueueLavaSeedKey,
    queueFluidSeedsForChunkAndReadyNeighbors,
    updateWaterFlow
  };
}
